export default class MapHandler {
	init() {
		const mapElement = document.getElementById('custom-map');
		if (!mapElement) return;

		// Get locations from data attribute
		const locationsData = mapElement.getAttribute('data-locations');
		if (!locationsData) {
			console.warn('No locations data attribute found');
			return;
		}

		let mapLocations;
		try {
			mapLocations = JSON.parse(locationsData);
		} catch (e) {
			console.error('Failed to parse locations data:', e);
			return;
		}

		if (!mapLocations || !mapLocations.length) {
			console.warn('No locations found for map');
			return;
		}

		if (typeof L === 'undefined') {
			console.error('Leaflet is not loaded');
			return;
		}

		// Initialize map - center on first location or default
		const centerLat = mapLocations[0] && mapLocations[0].lat ? mapLocations[0].lat : 51.5074;
		const centerLng = mapLocations[0] && mapLocations[0].lng ? mapLocations[0].lng : -0.1278;

		const map = L.map('custom-map', {
			center: [20, 0],
			zoom: 2,
			minZoom: 2,
			maxZoom: 5,
			zoomControl: true,
			scrollWheelZoom: false,
			maxBounds: [[-90, -200], [90, 200]],
			maxBoundsViscosity: 0.5
		});

		// Get the template directory URL from the data attribute
		const templateUrl = mapElement.dataset.templateUrl || '';
		const svgUrl = templateUrl + '/_dist/images/map-02.svg';
		const carretSvg = templateUrl + '/_dist/images/learnCarret.svg';

		const imageBounds = [
			[-50, -175], // Southwest corner
			[75, 185]    // Northeast corner
		];

		// Optional: Add a solid background color beneath
		const backgroundColor = L.tileLayer('data:image/svg+xml;base64,' + btoa(
			'<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">' +
			'<rect width="256" height="256" fill="#f4f6fb"/>' +
			'</svg>'
		), {
			attribution: 'Custom Map'
		});
		backgroundColor.addTo(map);

		// Add the SVG as an image overlay
		const svgOverlay = L.imageOverlay(svgUrl, imageBounds, {
			opacity: 1,
			alt: 'Custom Map Background'
		});
		svgOverlay.addTo(map);

		// Make sure overlay is on top
		svgOverlay.bringToFront();

		// Custom SVG marker icon
		const markerSvg = '<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">' +
			'<circle cx="20" cy="20" r="18" fill="#0A173D"/>' +
			'<circle cx="20" cy="20" r="8" fill="#61C7F2"/>' +
			'</svg>';

		const customIcon = L.divIcon({
			className: 'custom-marker',
			html: markerSvg,
			iconSize: [40, 40],
			iconAnchor: [20, 20],
			popupAnchor: [0, -20]
		});

		// Store all markers with their data
		const allMarkers = [];
		const bounds = [];

		// Add markers
		for (let i = 0; i < mapLocations.length; i++) {
			const location = mapLocations[i];

			const marker = L.marker([location.lat, location.lng], {
				icon: customIcon
			}).addTo(map);

			// Create popup content with image and regions
			let popupContent = '<div class="custom-popup">';

			// Add thumbnail image if available
			if (location.thumbnail) {
				popupContent += '<div class="popup-image"><img src="' + location.thumbnail + '" alt="' + location.title + '"></div>';
			}

			popupContent += '<div class="p-xs flex flex-col gap-xs">';
			popupContent += '<h3 class="font-primary font-medium text-sm !leading-none text-primary-2">' + location.title + '</h3>';

			// Add parent and child regions
			if (location.child_region || location.parent_region) {
				popupContent += '<div class="flex flex-col gap-2 text-xs text-1-40 [&_p]:!m-0">';
				if (location.parent_region) {
					popupContent += '<p>' + location.parent_region + '</p>';
				}
				if (location.child_region) {
					popupContent += '<p>' + location.child_region + '</p>';
				}
				popupContent += '</div>';
			}

			popupContent += '<a href="#location-' + location.post_id + '" class="popup-link button-secondary text-xxs !text-primary-2 font-" data-scroll-to="location-' + location.post_id + '">Learn more <div class="w-1.25"><img src="' + carretSvg + '" alt=""></div></a>';
			popupContent += '</div></div>';

			marker.bindPopup(popupContent, {
				maxWidth: 450,
				minWidth: 400
			});

			// Store marker with its data for filtering
			allMarkers.push({
				marker: marker,
				data: location
			});

			bounds.push([location.lat, location.lng]);
		}

		// Fit map to show all markers
		if (bounds.length > 1) {
			map.fitBounds(bounds, { padding: [50, 50] });
		}

		// Add scroll-to functionality for popup links
		// Use event delegation since popups are dynamically created
		document.addEventListener('click', (e) => {
			const scrollLink = e.target.closest('[data-scroll-to]');
			if (scrollLink) {
				e.preventDefault();
				const targetId = scrollLink.getAttribute('data-scroll-to');
				const targetElement = document.getElementById(targetId);

				if (targetElement) {
					// Close the popup
					map.closePopup();

					// Import lenis and scroll to target
					import('../global/scroll').then(({ lenis }) => {
						lenis.scrollTo(targetElement, {
							offset: -100,
							duration: 1.5
						});

						// Open the accordion after scrolling
						setTimeout(() => {
							const trigger = targetElement.querySelector('[data-accordion="trigger"]');
							if (trigger && !targetElement.classList.contains('is-active')) {
								trigger.click();
							}
						}, 1600);
					});
				}
			}
		});

		// Filtering functionality
		let activeServiceType = null;
		let activeRegion = null;

		// Filter markers based on active filters
		function filterMarkers() {
			allMarkers.forEach(({ marker, data }) => {
				let showMarker = true;

				// Check service type filter
				if (activeServiceType && data.service_types) {
					showMarker = data.service_types.includes(activeServiceType);
				}

				// Check region filter
				if (activeRegion && showMarker) {
					showMarker = data.parent_region === activeRegion;
				}

				// Show or hide marker
				if (showMarker) {
					if (!map.hasLayer(marker)) {
						marker.addTo(map);
					}
				} else {
					if (map.hasLayer(marker)) {
						map.removeLayer(marker);
					}
				}
			});
		}

		// Service type filter buttons
		const serviceButtons = document.querySelectorAll('.service-filter-btn');
		serviceButtons.forEach(btn => {
			btn.addEventListener('click', () => {
				const serviceType = btn.getAttribute('data-service-type');

				// Toggle active state
				if (activeServiceType === serviceType) {
					activeServiceType = null;
					btn.classList.remove('bg-primary-2', 'text-primary-1');
				} else {
					// Remove active from all service buttons
					serviceButtons.forEach(b => b.classList.remove('bg-primary-2', 'text-primary-1'));
					activeServiceType = serviceType;
					btn.classList.add('bg-primary-2', 'text-primary-1');
				}

				filterMarkers();
			});
		});

		/**
		 * Tab bar region filters - Disabled per client request
		 * Kept for future integration if needed
		 * Related PHP: locations_map.php lines 72-106 (also commented out)
		 */
		/*
		const tabBarContainer = document.querySelector('[data-target="locations-map"]');
		if (tabBarContainer) {
			const tabButtons = tabBarContainer.querySelectorAll('[data-tab-button]');
			tabButtons.forEach(btn => {
				btn.addEventListener('click', () => {
					const tabText = btn.textContent.trim();

					// First tab is typically "All" or similar - reset region filter
					const tabIndex = btn.getAttribute('data-tab-button');
					if (tabIndex === '0') {
						activeRegion = null;
					} else {
						activeRegion = tabText;
					}

					filterMarkers();
				});
			});
		}
		*/
	}
}
